import { AfterViewInit, Component, DestroyRef, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CurrencyPipe } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatButton } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { firstValueFrom, merge, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { isDeclinedRefusal } from 'core/api/api-model';
import { NotificationService } from 'shared/services/notification.service';
import { OverlayService } from 'shared/services/overlay.service';
import { CryptService } from '../../../../shared/services/crypt.service';
import { UserStore } from '../../../user/store/user.store';
import { AchMethodModel, PaymentMethodModel } from '../../../customer/customer.model';
import { OneTimePaymentComponent } from '../../components/one-time-payment/one-time-payment.component';
import { QuickPayService } from '../../services/quick-pay.service';
import { QuickPayCostOfCapital, QuickPayLookupResult, quickPayCostOfCapital, quickPayNotice } from '../../quick-pay.model';
import { CostOfCapitalGuardService } from 'app/services/cost-of-capital-guard.service';
import { buildOneTimePaymentAccount } from '../../utils/one-time-payment-account';

/**
 * Quick Pay: one screen for the walk-in or call-in customer — customer number, invoice number,
 * amount, a one-time card or ACH account and an optional comment, processed together.
 *
 * Neither number has to exist. The lookup says what the payment will become as the operator types;
 * an unmatched one is still taken, recorded against the profile's default customer and listed as
 * Unallocated on the A/R dashboard until someone confirms who and what it paid. Success lands on
 * the ordinary Collect Payment receipt.
 */
@Component({
  selector: 'app-quick-pay',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CurrencyPipe,
    MatButton,
    MatFormFieldModule,
    MatInputModule,
    MatIcon,
    MatProgressSpinnerModule,
    OneTimePaymentComponent,
  ],
  templateUrl: './quick-pay.component.html',
  // Styled globally (styles/features/payment/pages/_payment-quick-pay.scss): the page reaches into
  // OneTimePaymentComponent, whose grid was built for Collect's wide stepper.
  host: { class: 'page page-payment-quick-pay' },
})
export class QuickPayComponent implements OnInit, AfterViewInit {
  private readonly quickPay = inject(QuickPayService);
  private readonly cryptService = inject(CryptService);
  private readonly userStore = inject(UserStore);
  private readonly router = inject(Router);
  private readonly notificationService = inject(NotificationService);
  private readonly overlayService = inject(OverlayService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cocGuard = inject(CostOfCapitalGuardService);

  @ViewChild(OneTimePaymentComponent) oneTimePayment?: OneTimePaymentComponent;

  readonly form = new FormGroup({
    customerNumber: new FormControl<string>('', { nonNullable: true, validators: [Validators.maxLength(100)] }),
    invoiceNumber: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(100)],
    }),
    amount: new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]),
    comment: new FormControl<string>('', { nonNullable: true, validators: [Validators.maxLength(500)] }),
  });

  readonly lookup = signal<QuickPayLookupResult | null>(null);
  readonly lookingUp = signal(false);
  readonly paymentValid = signal(false);
  readonly submitting = signal(false);
  /** Quick Pay cannot run without its settings; the lookup's 403/409 says why. */
  readonly unavailable = signal<string | null>(null);
  /** The card's billing state is on the profile's no-cost-of-capital list. */
  readonly cocBlockedState = signal<string | null>(null);

  /** Re-evaluated as the typed values change, so the notice tracks the form. */
  private readonly typed = signal({ customer: '', invoice: '' });
  readonly notice = computed(() => quickPayNotice(this.lookup(), this.typed().customer, this.typed().invoice));

  ngOnInit(): void {
    // The first lookup names the default customer, which pre-fills the customer number.
    this.quickPay
      .lookup('', '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => {
          this.lookup.set(r);
          const acct = r.defaultCustomer.companyAccountNumber ?? '';
          if (!this.form.controls.customerNumber.value) this.form.controls.customerNumber.setValue(acct);
        },
        // 403 = Quick Pay is off for this profile, 409 = its default customer is gone. Anything else is
        // a failed lookup, which must not block taking a payment — the next keystroke retries it.
        error: (err) => {
          const code = err?.data?.code ?? err?.status;
          if (code === 403 || code === 409) {
            this.unavailable.set(this.messageOf(err) || 'Quick Pay is not available for this profile.');
          }
        },
      });

    merge(this.form.controls.customerNumber.valueChanges, this.form.controls.invoiceNumber.valueChanges)
      .pipe(
        map(() => ({
          customer: this.form.controls.customerNumber.value,
          invoice: this.form.controls.invoiceNumber.value,
        })),
        tap((t) => this.typed.set(t)),
        debounceTime(350),
        distinctUntilChanged((a, b) => a.customer.trim() === b.customer.trim() && a.invoice.trim() === b.invoice.trim()),
        tap(() => this.lookingUp.set(true)),
        switchMap((t) => this.quickPay.lookup(t.customer, t.invoice).pipe(catchError(() => of(null)))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((r) => {
        this.lookingUp.set(false);
        if (!r) return;
        this.lookup.set(r);
        // A found invoice pre-fills what it owes — only into an empty amount, never over one typed.
        if (r.invoice?.payable && r.invoice.outstandingAmount > 0 && !this.form.controls.amount.value) {
          this.form.controls.amount.setValue(r.invoice.outstandingAmount);
        }
      });
  }

  ngAfterViewInit(): void {
    // The state block keys on the card's billing zip, which lives inside the one-time entry.
    this.oneTimePayment?.form.controls.zipCode.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        switchMap((zip) => this.cocGuard.checkBlocked(zip ?? '')),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((r) => this.cocBlockedState.set(r.blocked ? r.state : null));
  }

  /**
   * The cost of capital FTE will add — a preview. FTE prices it itself from the gateway's view of
   * the card (credit only), so what is charged is what FTE decides; this mirrors its rule.
   */
  get costOfCapital(): QuickPayCostOfCapital {
    const otp = this.oneTimePayment;
    return quickPayCostOfCapital(
      this.userStore.activeProfile()?.profileDetail?.profileAdminSettings as any,
      { paymentType: otp?.paymentType, cardFunding: otp?.cardFunding, cardType: otp?.cardType },
      Number(this.form.controls.amount.value || 0),
      !!this.cocBlockedState()
    );
  }

  get canSubmit(): boolean {
    return this.form.valid && this.paymentValid() && !this.submitting() && !this.unavailable();
  }

  exit(): void {
    this.router.navigate(['/dashboard']);
  }

  async submit(): Promise<void> {
    if (!this.canSubmit || !this.oneTimePayment) return;
    this.submitting.set(true);
    const raw = this.form.getRawValue();
    try {
      const requireCVV = this.userStore.activeProfile()?.profileDetail?.profileAdminSettings?.requireCVV ?? true;
      const account = buildOneTimePaymentAccount(this.paymentMethodFromForm(), requireCVV);
      const encryptedPaymentAccount = await this.cryptService.encryptData(JSON.stringify(account));
      this.overlayService.showOverlay();
      const transaction = await firstValueFrom(
        this.quickPay.charge({
          customerNumber: raw.customerNumber.trim(),
          invoiceNumber: raw.invoiceNumber.trim(),
          amount: Number(raw.amount),
          comment: raw.comment.trim() || null,
          encryptedPaymentAccount,
        })
      );
      this.overlayService.hideOverlay();
      this.notificationService.success('Payment processed.', 'ok', 3000);
      this.router.navigate(['/payment/collect-success'], {
        queryParams: { view: 'onetimepay', transactionId: transaction.transactionId },
      });
    } catch (error: any) {
      this.overlayService.hideOverlay();
      this.handleError(error);
    } finally {
      this.submitting.set(false);
    }
  }

  /** The one-time entry as the shared account builder reads it — the same mapping Collect uses. */
  private paymentMethodFromForm(): PaymentMethodModel {
    const d = this.oneTimePayment!.getFormData();
    if (d.paymentType === 'ACH') {
      return {
        tokenType: 'ACH',
        account: d.account,
        abaRoute: d.abaRoute,
        accountHolderName: d.accountHolderName,
        accountHolderFirstName: d.accountHolderFirstName,
        accountHolderLastName: d.accountHolderLastName,
        accountType: d.accountType,
      } as AchMethodModel;
    }
    return {
      account: d.account,
      cardHolderName: d.cardHolderName,
      expirationDate: d.expirationDate,
      cvv: d.cvv,
      streetAddress: d.streetAddress,
      zipCode: d.zipCode,
    } as PaymentMethodModel;
  }

  private handleError(error: any): void {
    // A charge the gateway declined has a transaction — show it on the result page, as Collect does.
    const failedTransactionId = error?.data?.transactionResponse?.transactionId;
    if (failedTransactionId) {
      this.router.navigate(['/payment/collect-success'], {
        queryParams: { view: 'error', transactionId: failedTransactionId },
      });
      return;
    }
    if (isDeclinedRefusal(error?.data)) {
      this.router.navigate(['/payment/collect-success'], { queryParams: { view: 'declined' } });
      return;
    }
    this.notificationService.error(this.messageOf(error) || 'Something went wrong. Please try again.', 'ok', 8000);
  }

  /**
   * FTE's own words when it gave any — the transport message says nothing useful. A sentence wins
   * over a code: FTE puts the reason in `message` and often a bare code (`QUICK_PAY_DISABLED`) in
   * `details`, while a sale's refusal carries the sentence in `details` instead.
   */
  private messageOf(error: any): string {
    const isSentence = (v: unknown): v is string => typeof v === 'string' && v.trim().includes(' ');
    const details = error?.data?.details;
    const first = Array.isArray(details) ? details[0] : null;
    if (isSentence(error?.data?.message)) return error.data.message;
    if (isSentence(first)) return first;
    return error?.message || '';
  }

}
